/* eslint-disable @typescript-eslint/no-explicit-any */
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {StackNavigationProp} from '@react-navigation/stack';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import React, {useCallback, useMemo} from 'react';
import {Image, TouchableOpacity, View} from 'react-native';
import {connect} from 'react-redux';
import {getCountryCodeFromName} from '../../../../../common/utils/countryUtils';
import {ValidationSouscription} from '../../../../../common/utils/validationAssuranceRapatriement';
import {TitleLabels} from '../../../../../data/constants/Assurance/AssuranceRapatriement/souscription';
import {sexes} from '../../../../../data/constants/profil';
import {IValues} from '../../../../../data/types/interface/Assurance/AssuranceRapatriement/souscription';
import {StackParams} from '../../../../../data/types/navigation';
import {useFormSouscription} from '../../../../../hooks/AssuranceRapatriement/useFormSouscription';
import mapDispatchToProps from '../../../../../services/redux/mapDispatchToProps';
import mapStateToProps from '../../../../../services/redux/mapStateToProps';
import {BoxShadowed} from '../../../../components/AssuranceVoyage';
import Button from '../../../../components/Button/PrimaryButton';
import SubmitButtons from '../../../../components/Button/SecondaryButton';
import {InputDatePicker} from '../../../../components/Inputs/InputDatePicker';
import {InputField} from '../../../../components/Inputs/InputField';
import {InputImage} from '../../../../components/Inputs/InputImage';
import {InputPhoneNumber} from '../../../../components/Inputs/InputPhoneNumber/index.web';
import Select from '../../../../components/Inputs/Select';
import {CountrySelect} from '../../../../components/Inputs/Select/CountrySelect';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal/index.web';
import {CustomSwitch} from '../../../../components/Switch';
import styles from '../styles';

interface FormProps {
  data: IValues;
  initData: IValues;
  setData: (value: IValues) => void;
  onCancelForm: () => void;
  onSubmitForm: (data: IValues) => void;
  user?: any;
}

const FormFC = ({
  data,
  initData,
  setData,
  onCancelForm,
  onSubmitForm,
  user,
}: FormProps) => {
  const {
    handleChange,
    handleSubmit,
    isDisable,
    handleCancel,
    values,
    errors,
    showErrors,
    handleAddAssured,
    handleRemoveAssured,
    handleChangeAssured,
    modalData,
    handleInitValues,
  } = useFormSouscription(
    data,
    setData,
    initData,
    ValidationSouscription,
    onCancelForm,
    onSubmitForm,
    user?.data?.utilisateur,
  );

  const ageMax = 80;

  const currentUser = user?.data?.utilisateur;
  const {navigate} = useNavigation<StackNavigationProp<StackParams>>();

  useFocusEffect(
    useCallback(() => {
      if (!currentUser) {
        navigate('Login', {
          data: 'SouscriptionAssuranceRapatriement',
          params: {
            dateDebut: values.dateDebut,
            beneficiaires: values.assure,
          },
        });
      }
    }, [currentUser]),
  );

  useFocusEffect(
    React.useCallback(() => {
      handleInitValues();
    }, []),
  );

  const enableSwitch = useMemo(() => {
    return values.assure[0]?.isEditable;
  }, [values]);

  return (
    <View>
      <InputDatePicker
        label={'Début du contrat souhaité'}
        required
        name="dateDebut"
        value={values.dateDebut}
        onChange={(name: string, value: any, fired: boolean) =>
          handleChange('dateDebut', value, fired)
        }
        error={errors.dateDebut}
        showError={showErrors.dateDebut}
        isEditable={!values.isRenewal}
      />
      <View>
        <CustomSwitch
          label="Assuré"
          name="userIsAssured"
          required
          onChange={(name: string, value: boolean) =>
            handleChange(name as keyof IValues, value, true)
          }
          value={values.userIsAssured}
          style={enableSwitch ? {} : {opacity: 0.5}}
          disabled={!enableSwitch}
        />
      </View>

      {modalData.status ? (
        <ConfirmationModal
          title={modalData.title}
          content={modalData.content}
          btn1={modalData.btn1}
          btn2={modalData.btn2}
          handleBtn1={modalData.handleBtn1}
          handleBtn2={modalData.handleBtn2}
        />
      ) : null}
      {values.assure.length
        ? values.assure.map((assure, index: number) => {
            return (
              <BoxShadowed
                style={{paddingBottom: 40, marginBottom: 40}}
                key={`${assure.id}`}>
                {values.userIsAssured && index === 0 ? null : (
                  <TouchableOpacity
                    style={styles.closeBtn}
                    onPress={() => handleRemoveAssured(assure.id)}>
                    <Image
                      style={{width: 40, height: 40}}
                      // eslint-disable-next-line @typescript-eslint/no-require-imports
                      source={require('../../../../../ressources/images/close-1.png')}
                    />
                  </TouchableOpacity>
                )}
                <View>
                  <InputField
                    label={'Nom'}
                    required
                    value={assure.nom}
                    name="nom"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.nom
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.nom
                    }
                    maxLength={70}
                    isEditable={
                      assure.isEditable &&
                      (!values.userIsAssured || index !== 0)
                    }
                  />
                  <InputField
                    label={'Prénom'}
                    required
                    value={assure.prenom}
                    name="prenom"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.prenom
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.prenom
                    }
                    maxLength={70}
                    isEditable={
                      assure.isEditable &&
                      (!values.userIsAssured || index !== 0)
                    }
                  />
                  <Select
                    style={{flex: 0.45, marginBottom: 30}}
                    data={sexes}
                    label="Sexe"
                    defaultValue={assure.sexe}
                    required={true}
                    name="sexe"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    value={assure.sexe}
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.sexe
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.sexe
                    }
                    disabled={!assure.isEditable}
                  />
                  <InputDatePicker
                    label={TitleLabels.contractant.dateNaissanceBeneficiaire}
                    required
                    name="dateNaissance"
                    value={assure.dateNaissance}
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.dateNaissance
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.dateNaissance
                    }
                    isEditable={
                      assure.isEditable &&
                      (!values.userIsAssured || index !== 0)
                    }
                    dateMin={dayjs()
                      .subtract(ageMax, 'year')
                      .add(1, 'day')
                      .toDate()}
                    dateMax={dayjs().toDate()}
                    noInit
                  />

                  <CountrySelect
                    label={'Pays de résidence'}
                    required
                    value={assure.paysResidence}
                    name="paysResidence"
                    exceptCode={['MG']}
                    onChange={(value: string) => {
                      handleChangeAssured(
                        assure.id,
                        'paysResidence',
                        value,
                        true,
                      );

                      if (value) {
                        const newCountryCode = getCountryCodeFromName(value);
                        handleChangeAssured(
                          assure.id,
                          'countryCode',
                          newCountryCode,
                          true,
                        );
                      }
                    }}
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.paysResidence
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.paysResidence
                    }
                    isEditable={assure.isEditable}
                  />
                  <InputField
                    label={'Ville'}
                    required
                    value={assure.ville}
                    name="ville"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.ville
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.ville
                    }
                    maxLength={70}
                    isEditable={assure.isEditable}
                  />
                  <InputField
                    label={'Adresse postale'}
                    required
                    value={assure.adressePostale}
                    name="adressePostale"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.adressePostale
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.adressePostale
                    }
                    maxLength={70}
                    isEditable={assure.isEditable}
                  />
                  <InputField
                    label={'Email'}
                    required
                    value={assure.email}
                    name="email"
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.email
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.email
                    }
                    maxLength={70}
                    isEditable={
                      assure.isEditable &&
                      (!values.userIsAssured || index !== 0)
                    }
                    placeholder="Email de l'assuré ou d'un parent"
                  />

                  <InputPhoneNumber
                    label={'Numéro de Téléphone'}
                    required
                    name="telephone"
                    type="telephone"
                    value={assure.telephone}
                    onChange={(name: string, value: string, fired: any) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.telephone
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.telephone
                    }
                    country={assure?.countryCode || 'FR'}
                    onChangeCountry={(newCountry: string) =>
                      handleChangeAssured(
                        assure.id,
                        'countryCode',
                        newCountry,
                        true,
                      )
                    }
                    isRapatriement={true}
                    isEditable={assure.isEditable}
                  />

                  <InputField
                    style={{flex: 1}}
                    label={"Numéro de carte d'identité / de séjour"}
                    type="numPasseport"
                    required
                    name="numCI"
                    value={assure.numCI}
                    onChange={(name: string, value: any, fired: boolean) =>
                      handleChangeAssured(assure.id, name, value, fired)
                    }
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.numCI
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.numCI
                    }
                    isEditable={assure.isEditable}
                  />
                  <InputImage
                    style={{flex: 0.5, justifyContent: 'flex-end'}}
                    label={`Joindre une copie de carte d'identité / séjour`}
                    required
                    name="capture_file"
                    value={
                      assure.capture_file &&
                      typeof assure.capture_file != 'string'
                        ? assure.capture_file
                        : []
                    }
                    onChange={(
                      name: string,
                      value: any,
                      fired: boolean,
                      remove?: boolean,
                    ) => {
                      return handleChangeAssured(
                        assure.id,
                        name,
                        value,
                        fired,
                        remove,
                      );
                    }}
                    error={
                      errors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.capture_file
                    }
                    showError={
                      showErrors.assure.find(
                        (element: any) => element.id === assure.id,
                      )?.capture_file
                    }
                    maxLength={1}
                    isEditable={assure.isEditable}
                  />
                </View>
              </BoxShadowed>
            );
          })
        : null}
      <View style={styles.submitButtonsContainer}>
        <Button
          onPress={() => handleAddAssured()}
          styleBtnTxt={[styles.btnValider, {textAlign: 'center'}]}
          styleBtnOuter={[
            styles.btnValiderOuter,
            {maxWidth: 100, marginTop: 30},
          ]}
          styleContainer={styles.noFlexGrow}
          title={'Ajouter Assuré'}
          type="valider"
        />
        <SubmitButtons
          isDisable={!isDisable}
          cancelAction={handleCancel}
          submitAction={handleSubmit}
          cancelTitle="Annuler"
          submitTitle="Valider"
        />
      </View>
    </View>
  );
};
export const Form = connect(mapStateToProps, mapDispatchToProps)(FormFC);
